/**
 * Detector Paper Trading Dashboard
 * ==================================
 * Auto paper-trade portfolio from detector top picks.
 * Shows equity curve, stats, open positions, trade history, monthly returns.
 *
 * API: /api/paper-trading/*
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Skeleton, alpha, Chip, Tab, Tabs,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Paper,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  ShowChart as ShowChartIcon,
  Assessment as AssessmentIcon,
  Speed as SpeedIcon,
  SwapHoriz as SwapIcon,
  AccessTime as TimeIcon,
  GpsFixed as TargetIcon,
  Shield as StopIcon,
  Timeline as TrailIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  Area, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, ReferenceLine, ComposedChart, Line,
} from 'recharts';
import { detectorPaperTradingAPI } from '../services/api';

// ━━ Colors ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const C = {
  green: '#10b981',
  red: '#ef4444',
  amber: '#f59e0b',
  blue: '#3b82f6',
  purple: '#8b5cf6',
  slate: '#64748b',
  bg: '#f8fafc',
};

// ━━ Number formatters ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const fmtVND = (v) => {
  if (v === null || v === undefined) return '—';
  if (Math.abs(v) >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  return v.toLocaleString('vi-VN');
};
const fmtPct = (v, digits = 2) => v !== null && v !== undefined ? `${v >= 0 ? '+' : ''}${v.toFixed(digits)}%` : '—';
const fmtPrice = (v) => v ? v.toLocaleString('vi-VN', { maximumFractionDigits: 0 }) : '—';

// ━━ Stat Tile ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const Tile = ({ label, value, sub, color, Icon }) => (
  <Box sx={{
    position: 'relative', overflow: 'hidden', borderRadius: 2.5,
    border: `1px solid ${alpha(color, 0.15)}`, bgcolor: '#fff',
    p: '14px 16px', transition: 'all 0.2s',
    '&:hover': {
      borderColor: alpha(color, 0.3),
      boxShadow: `0 4px 16px ${alpha(color, 0.08)}`,
      transform: 'translateY(-1px)',
    },
  }}>
    <Box sx={{
      position: 'absolute', top: 0, right: 0, width: 64, height: 64,
      background: `radial-gradient(circle at top right, ${alpha(color, 0.06)} 0%, transparent 70%)`,
    }} />
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{
        width: 38, height: 38, borderRadius: 2, flexShrink: 0,
        background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 3px 8px ${alpha(color, 0.25)}`,
      }}>
        <Icon sx={{ fontSize: 20, color: '#fff' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', mt: 0.3 }}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.2 }}>
            {sub}
          </Typography>
        )}
      </Box>
    </Box>
  </Box>
);

// ━━ Exit reason chip ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const EXIT_CONFIG = {
  target:   { icon: TargetIcon, color: C.green, label: 'Target' },
  stop:     { icon: StopIcon, color: C.red, label: 'Stop' },
  trailing: { icon: TrailIcon, color: C.blue, label: 'Trail' },
  time:     { icon: TimeIcon, color: C.amber, label: 'Time' },
};

const ExitChip = ({ reason }) => {
  const cfg = EXIT_CONFIG[reason] || { icon: SwapIcon, color: C.slate, label: reason };
  const Icon = cfg.icon;
  return (
    <Chip
      icon={<Icon sx={{ fontSize: 14 }} />}
      label={cfg.label}
      size="small"
      sx={{
        fontWeight: 700, fontSize: '0.68rem', height: 24,
        bgcolor: alpha(cfg.color, 0.1),
        color: cfg.color,
        border: `1px solid ${alpha(cfg.color, 0.2)}`,
        '& .MuiChip-icon': { color: cfg.color },
      }}
    />
  );
};

// ━━ Custom Recharts Tooltip ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const EquityTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <Box sx={{
      bgcolor: 'rgba(15,23,42,0.92)', color: '#fff', borderRadius: 1.5,
      p: '8px 12px', fontSize: '0.72rem', lineHeight: 1.6,
      backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.08)',
    }}>
      <Box sx={{ fontWeight: 700, mb: 0.5 }}>{d.date}</Box>
      <Box>Equity: <b>{fmtVND(d.equity)}</b></Box>
      <Box>Cash: {fmtVND(d.cash)}</Box>
      <Box>Return: <span style={{ color: d.cumulative_return_pct >= 0 ? C.green : C.red }}>{fmtPct(d.cumulative_return_pct)}</span></Box>
      <Box>DD: <span style={{ color: d.drawdown_pct < -5 ? C.red : C.amber }}>{fmtPct(d.drawdown_pct)}</span></Box>
      <Box>Open: {d.open_positions} | +{d.trades_opened} -{d.trades_closed}</Box>
    </Box>
  );
};

const MonthlyTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <Box sx={{
      bgcolor: 'rgba(15,23,42,0.92)', color: '#fff', borderRadius: 1.5,
      p: '8px 12px', fontSize: '0.72rem',
      backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.08)',
    }}>
      <Box sx={{ fontWeight: 700 }}>{d.month}</Box>
      <Box>Return: <span style={{ color: d.return_pct >= 0 ? C.green : C.red }}>{fmtPct(d.return_pct)}</span></Box>
      <Box>Equity: {fmtVND(d.equity_start)} → {fmtVND(d.equity_end)}</Box>
      <Box>Max DD: {fmtPct(d.max_drawdown_pct)}</Box>
    </Box>
  );
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Main Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export default function DetectorPaperTrading() {
  useTranslation();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState(null);
  const [curve, setCurve] = useState([]);
  const [positions, setPositions] = useState([]);
  const [trades, setTrades] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, curveRes, posRes, tradesRes] = await Promise.all([
        detectorPaperTradingAPI.getStats(120),
        detectorPaperTradingAPI.getEquityCurve(120),
        detectorPaperTradingAPI.getPositions(),
        detectorPaperTradingAPI.getTrades(100),
      ]);
      setStats(statsRes.data);
      setCurve(curveRes.data?.data || []);
      setPositions(posRes.data?.positions || []);
      setTrades(tradesRes.data?.trades || []);
    } catch (err) {
      console.error('Error loading paper trading data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Skeleton loading ──
  if (loading) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        <Skeleton variant="rounded" width={300} height={36} sx={{ mb: 2 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 2, mb: 3 }}>
          {[...Array(6)].map((_, i) => <Skeleton key={i} variant="rounded" height={90} />)}
        </Box>
        <Skeleton variant="rounded" height={340} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={300} />
      </Box>
    );
  }

  const s = stats || {};
  const capital = s.capital || {};
  const returns = s.returns || {};
  const tradeStats = s.trades || {};
  const monthly = s.monthly_returns || [];
  const bySector = s.by_sector || [];
  const bySetup = s.by_setup || [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            background: `linear-gradient(135deg, ${C.purple} 0%, ${alpha(C.purple, 0.7)} 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 3px 8px ${alpha(C.purple, 0.3)}`,
          }}>
            <ShowChartIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b' }}>
              Detector Paper Trading
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
              Auto paper-trade top quality picks · {s.period?.start} → {s.period?.end} · {s.period?.trading_days || 0} days
            </Typography>
          </Box>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadData}
          size="small"
          sx={{
            bgcolor: alpha(C.purple, 0.08), color: C.purple,
            fontWeight: 700, fontSize: '0.75rem', borderRadius: 2, px: 2,
            '&:hover': { bgcolor: alpha(C.purple, 0.15) },
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* ── Stat Tiles ── */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
        gap: 1.5, mb: 2.5,
      }}>
        <Tile label="Total Return" value={fmtPct(returns.total_return_pct)} sub={`Ann: ${fmtPct(returns.annualized_return_pct)}`} color={returns.total_return_pct >= 0 ? C.green : C.red} Icon={TrendingUpIcon} />
        <Tile label="Sharpe Ratio" value={returns.sharpe_ratio?.toFixed(2) || '—'} sub={`Max DD: ${fmtPct(returns.max_drawdown_pct)}`} color={C.blue} Icon={SpeedIcon} />
        <Tile label="Win Rate" value={`${tradeStats.win_rate_pct?.toFixed(1) || 0}%`} sub={`${tradeStats.total_closed || 0} trades`} color={tradeStats.win_rate_pct >= 50 ? C.green : C.amber} Icon={AssessmentIcon} />
        <Tile label="Profit Factor" value={tradeStats.profit_factor?.toFixed(2) || '—'} sub={`Avg: ${fmtPct(tradeStats.avg_return_pct)}`} color={tradeStats.profit_factor >= 1.5 ? C.green : C.amber} Icon={SwapIcon} />
        <Tile label="Equity" value={fmtVND(capital.current_equity)} sub={`Cash: ${fmtVND(capital.current_cash)}`} color={C.purple} Icon={AccountBalanceIcon} />
        <Tile label="Open" value={capital.open_positions || 0} sub={`PnL: ${fmtVND(capital.total_pnl_vnd)}`} color={C.slate} Icon={ShowChartIcon} />
      </Box>

      {/* ── Tabs ── */}
      <Paper sx={{ borderRadius: 2.5, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc', minHeight: 42,
            '& .MuiTab-root': { minHeight: 42, fontWeight: 700, fontSize: '0.78rem', textTransform: 'none' },
            '& .Mui-selected': { color: C.purple },
            '& .MuiTabs-indicator': { backgroundColor: C.purple },
          }}
        >
          <Tab label="Equity Curve" />
          <Tab label={`Positions (${positions.length})`} />
          <Tab label={`Trade History (${trades.length})`} />
          <Tab label="Monthly" />
          <Tab label="By Sector" />
          <Tab label="By Setup" />
        </Tabs>

        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          {/* ── TAB 0: Equity Curve ── */}
          {tab === 0 && (
            <Box>
              <ResponsiveContainer width="100%" height={360}>
                <ComposedChart data={curve} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickFormatter={(v) => v?.slice(5) || ''}
                  />
                  <YAxis
                    yAxisId="equity" tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickFormatter={(v) => fmtVND(v)}
                    domain={['dataMin', 'dataMax']}
                  />
                  <YAxis
                    yAxisId="dd" orientation="right" tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickFormatter={(v) => `${v}%`}
                    domain={[-20, 2]}
                  />
                  <RTooltip content={<EquityTooltip />} />
                  <ReferenceLine yAxisId="equity" y={curve[0]?.equity || 1e9} stroke="#94a3b8" strokeDasharray="4 4" label={{ value: 'Start', fill: '#94a3b8', fontSize: 10 }} />
                  <Area yAxisId="equity" type="monotone" dataKey="equity" stroke={C.purple} fill={alpha(C.purple, 0.08)} strokeWidth={2.5} dot={false} name="Equity" />
                  <Line yAxisId="dd" type="monotone" dataKey="drawdown_pct" stroke={C.red} strokeWidth={1.5} dot={false} name="Drawdown %" strokeDasharray="4 4" />
                </ComposedChart>
              </ResponsiveContainer>
              {/* Exit reason summary */}
              {tradeStats.exit_reasons && (
                <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                  {Object.entries(tradeStats.exit_reasons).map(([k, v]) => (
                    <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ExitChip reason={k} />
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569' }}>{v}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* ── TAB 1: Open Positions ── */}
          {tab === 1 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem', color: '#64748b', bgcolor: '#f8fafc', py: 1.2, borderBottom: '2px solid #e2e8f0' } }}>
                    <TableCell>Ticker</TableCell>
                    <TableCell>Entry Date</TableCell>
                    <TableCell align="right">Entry Price</TableCell>
                    <TableCell align="right">Current</TableCell>
                    <TableCell align="right">Unrealized</TableCell>
                    <TableCell align="right">PnL VND</TableCell>
                    <TableCell>Quality</TableCell>
                    <TableCell>Setup</TableCell>
                    <TableCell>Sector</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positions.length === 0 && (
                    <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>No open positions</TableCell></TableRow>
                  )}
                  {positions.map((p) => {
                    const isWin = p.unrealized_pnl_pct > 0;
                    return (
                      <TableRow key={p.id} sx={{ '&:hover': { bgcolor: alpha(C.purple, 0.02) }, transition: 'background 0.15s' }}>
                        <TableCell sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.82rem' }}>{p.ticker}</TableCell>
                        <TableCell sx={{ fontSize: '0.76rem', color: '#64748b' }}>{p.entry_date}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{fmtPrice(p.entry_price)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{fmtPrice(p.current_price)}</TableCell>
                        <TableCell align="right" sx={{
                          fontWeight: 700, fontSize: '0.78rem',
                          color: isWin ? C.green : C.red,
                        }}>
                          {fmtPct(p.unrealized_pnl_pct)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.76rem', color: isWin ? C.green : C.red }}>
                          {fmtVND(p.unrealized_pnl_vnd)}
                        </TableCell>
                        <TableCell>
                          <Chip label={p.quality_score?.toFixed(3) || '—'} size="small" sx={{
                            fontWeight: 700, fontSize: '0.68rem', height: 22,
                            bgcolor: alpha(C.purple, 0.08), color: C.purple,
                          }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.72rem', color: '#64748b' }}>{p.setup_type}</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem', color: '#64748b' }}>{p.sector}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── TAB 2: Trade History ── */}
          {tab === 2 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem', color: '#64748b', bgcolor: '#f8fafc', py: 1.2, borderBottom: '2px solid #e2e8f0' } }}>
                    <TableCell>Ticker</TableCell>
                    <TableCell>Entry</TableCell>
                    <TableCell>Exit</TableCell>
                    <TableCell align="right">Entry ₫</TableCell>
                    <TableCell align="right">Exit ₫</TableCell>
                    <TableCell align="right">PnL %</TableCell>
                    <TableCell align="right">PnL VND</TableCell>
                    <TableCell>Exit</TableCell>
                    <TableCell align="right">Days</TableCell>
                    <TableCell>Quality</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trades.length === 0 && (
                    <TableRow><TableCell colSpan={10} sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>No trades</TableCell></TableRow>
                  )}
                  {trades.map((t) => {
                    const isWin = (t.pnl_pct || 0) > 0;
                    return (
                      <TableRow key={t.id} sx={{ '&:hover': { bgcolor: alpha(isWin ? C.green : C.red, 0.02) }, transition: 'background 0.15s' }}>
                        <TableCell sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.82rem' }}>{t.ticker}</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem', color: '#64748b' }}>{t.entry_date}</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem', color: '#64748b' }}>{t.exit_date}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.76rem' }}>{fmtPrice(t.entry_price)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.76rem' }}>{fmtPrice(t.exit_price)}</TableCell>
                        <TableCell align="right" sx={{
                          fontWeight: 700, fontSize: '0.82rem',
                          color: isWin ? C.green : C.red,
                        }}>
                          {fmtPct(t.pnl_pct)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.76rem', color: isWin ? C.green : C.red }}>
                          {fmtVND(t.pnl_amount)}
                        </TableCell>
                        <TableCell><ExitChip reason={t.exit_reason} /></TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.76rem' }}>{t.days_held}d</TableCell>
                        <TableCell>
                          <Chip label={t.quality_score?.toFixed(3) || '—'} size="small" sx={{
                            fontWeight: 700, fontSize: '0.68rem', height: 22,
                            bgcolor: alpha(C.purple, 0.08), color: C.purple,
                          }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── TAB 3: Monthly Returns ── */}
          {tab === 3 && (
            <Box>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={monthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(v) => `${v}%`} />
                  <RTooltip content={<MonthlyTooltip />} />
                  <ReferenceLine y={0} stroke="#94a3b8" />
                  <Bar dataKey="return_pct" radius={[4, 4, 0, 0]}>
                    {monthly.map((m, i) => (
                      <Cell key={i} fill={m.return_pct >= 0 ? C.green : C.red} fillOpacity={0.8} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {/* Monthly table */}
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem', color: '#64748b', bgcolor: '#f8fafc' } }}>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Return</TableCell>
                      <TableCell align="right">Equity Start</TableCell>
                      <TableCell align="right">Equity End</TableCell>
                      <TableCell align="right">Max DD</TableCell>
                      <TableCell align="right">Days</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {monthly.map((m) => (
                      <TableRow key={m.month}>
                        <TableCell sx={{ fontWeight: 700 }}>{m.month}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: m.return_pct >= 0 ? C.green : C.red }}>
                          {fmtPct(m.return_pct)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.76rem' }}>{fmtVND(m.equity_start)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.76rem' }}>{fmtVND(m.equity_end)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.76rem', color: C.red }}>{fmtPct(m.max_drawdown_pct)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.76rem' }}>{m.trading_days}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* ── TAB 4: By Sector ── */}
          {tab === 4 && (
            <Box>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={bySector} layout="vertical" margin={{ top: 10, right: 10, left: 80, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(v) => `${v}%`} />
                  <YAxis type="category" dataKey="sector" tick={{ fontSize: 11, fill: '#475569', fontWeight: 600 }} width={80} />
                  <RTooltip formatter={(v, name) => [name === 'avg_return_pct' ? fmtPct(v) : `${v}%`, name === 'avg_return_pct' ? 'Avg Return' : 'Win Rate']} />
                  <Bar dataKey="avg_return_pct" radius={[0, 4, 4, 0]} name="Avg Return">
                    {bySector.map((s, i) => (
                      <Cell key={i} fill={s.avg_return_pct >= 0 ? C.green : C.red} fillOpacity={0.8} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem', color: '#64748b', bgcolor: '#f8fafc' } }}>
                      <TableCell>Sector</TableCell>
                      <TableCell align="right">Trades</TableCell>
                      <TableCell align="right">Win Rate</TableCell>
                      <TableCell align="right">PF</TableCell>
                      <TableCell align="right">Avg Return</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bySector.map((s) => (
                      <TableRow key={s.sector}>
                        <TableCell sx={{ fontWeight: 700 }}>{s.sector}</TableCell>
                        <TableCell align="right">{s.trades}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: s.win_rate_pct >= 50 ? C.green : C.red }}>{s.win_rate_pct}%</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: s.profit_factor >= 1 ? C.green : C.red }}>{s.profit_factor}</TableCell>
                        <TableCell align="right" sx={{ color: s.avg_return_pct >= 0 ? C.green : C.red }}>{fmtPct(s.avg_return_pct)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* ── TAB 5: By Setup ── */}
          {tab === 5 && (
            <Box>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={bySetup} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="setup" tick={{ fontSize: 11, fill: '#475569', fontWeight: 600 }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(v) => `${v}%`} />
                  <RTooltip formatter={(v) => [fmtPct(v), 'Avg Return']} />
                  <ReferenceLine y={0} stroke="#94a3b8" />
                  <Bar dataKey="avg_return_pct" radius={[4, 4, 0, 0]}>
                    {bySetup.map((s, i) => (
                      <Cell key={i} fill={s.avg_return_pct >= 0 ? C.green : C.red} fillOpacity={0.8} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem', color: '#64748b', bgcolor: '#f8fafc' } }}>
                      <TableCell>Setup</TableCell>
                      <TableCell align="right">Trades</TableCell>
                      <TableCell align="right">Win Rate</TableCell>
                      <TableCell align="right">PF</TableCell>
                      <TableCell align="right">Avg Return</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bySetup.map((s) => (
                      <TableRow key={s.setup}>
                        <TableCell sx={{ fontWeight: 700 }}>{s.setup}</TableCell>
                        <TableCell align="right">{s.trades}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: s.win_rate_pct >= 50 ? C.green : C.red }}>{s.win_rate_pct}%</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: s.profit_factor >= 1 ? C.green : C.red }}>{s.profit_factor}</TableCell>
                        <TableCell align="right" sx={{ color: s.avg_return_pct >= 0 ? C.green : C.red }}>{fmtPct(s.avg_return_pct)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
